import React, {useEffect, useRef, useState} from 'react';
import { Template } from "../../../components/template/template.jsx";
import "../../../css/style-report.css";
import "../../../css/input-date-time.css";
import ReportUtils from "../../../utils/report-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import {Card} from "../../../components/card/card.jsx";
import {subYears} from "date-fns";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import Content from "./components/content.jsx";
import {useMutation} from "@tanstack/react-query";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import agentProductivityService from "../../../services/reports/agent-productivity-service.js";
import reportUtils from "../../../utils/report-utils.js";

const initialFilters = {
  dtStart: subYears(new Date(),1),
  dtEnd: new Date(),
  userId: "",
  leaderId: "",
  nature: [],
  natureGroup: [],
};

const filterLabels = {
  dtStart: "Data inicial",
  dtEnd: "Data final",
  userId: "Agente",
  leaderId: "Responsável",
  nature: "Natureza",
  natureGroup: "Grupo de natureza",
};

const AgentProductivity = () => {
  const [loading, setLoading] = useState(false);

  const [collapsed, setCollapsed] = useState(false);
  const buttonRef = useRef(null);
  const [fixedTop, setFixedTop] = useState(null);

  const [filters, setFilters] = useState(initialFilters);
  const [reportData, setReportData] = useState(null);

  const [details, setDetails] = useState(false);

  const [title] = useState("Produtividade de agentes");
  const [headerSelected, setHeaderSelected] = useState(-1);
  const printComponentRef = useRef(null);
  const [isLeader, setIsLeader] = useState(false);

  const mutation = useMutation({
    mutationFn: (payload) => agentProductivityService.apply(payload),
    onSuccess: data => setReportData(data),
  })

  useEffect(() => {
    if (!reportData) return;

    ReportUtils.appendFilters(
      ReportUtils.filters({
        ...filters,
        dtStart: filters.dtStart ? ReportUtils.formatDateOnly(filters.dtStart) : "",
        dtEnd: filters.dtEnd ? ReportUtils.formatDateOnly(filters.dtEnd) : "",
      }, filterLabels)
    );

  }, [reportData]);

  const apply = async () => {

    const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 1, "year");

    if (!periodValidation.ok) {
      ReportUtils.alertErrorMessage(periodValidation.message);
      setLoading(false);
      return;
    }

    if (!filters.userId && !filters.leaderId) {
      ReportUtils.alertErrorMessage(`Selecione um "Agente" ou um "Responsável"`);
      setLoading(false);
      return;
    }

    ReportUtils.applyLoad({
      setLoading,
      clearContent: () => {
        setReportData(null)
      }
    });

    try {
      setLoading(true)
      const formattedFilters = {
        dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
        dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
      };

      const auditFilters = ReportUtils.filters({
          ...filters,
          dtStart: formattedFilters.dtStart,
          dtEnd: formattedFilters.dtEnd,
        },
        filterLabels, true);

      setIsLeader(filters.leaderId);

      await mutation.mutateAsync({
        ...buildPayload(filters),
        filters: auditFilters
      });

      setDetails(filters.searchDetails);

      const scale = window.visualViewport?.scale ?? 1;
      if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

    } catch (error) {

      ReportUtils.alertErrorMessage("Erro ao gerar o relatório.");
      console.error("Erro ao gerar o relatório", error);

    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    const refDate = new Date;
    setFilters({...initialFilters,
                      dtStart: subYears(refDate, 1),
                      dtEnd: refDate});
    setReportData(null);
  }

  const toggleFilters = () => {
    if (!collapsed && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFixedTop(rect.top);
    }

    setCollapsed(prev => !prev);
  }

  useGlobalKey("Enter", apply);
  useGlobalKey("Escape", clearSearch);

  function buildPayload(data) {
    const payloadObj = {
      dtStart: reportUtils.formatDateOnly(data.dtStart),
      dtEnd: reportUtils.formatDateOnly(data.dtEnd),
    }

    if(filters.leaderId) payloadObj.leaderId = filters.leaderId.value;
    if(filters.userId) payloadObj.userId = filters.userId.value;
    if(filters.nature) payloadObj.nature = filters.nature.map(el => el.value);
    if(filters.natureGroup) payloadObj.natureGroup = filters.natureGroup.map(el => el.value);

    return payloadObj;
  }

  return (
    <SubScroll dependency={[reportData, collapsed]}>
      <div className={"flex"}>

        <Template isLoading={loading || mutation.isPending}>
          <Template.Header>
            <div className="flex justify-between w-full">
              <Template.Title>{title}</Template.Title>
              <HeaderButtons
                exportable={true}
                exportInSheets={true}
                printable={true}
                headerChange={true}
                printComponentRef={printComponentRef}
                reportData={reportData}
                data={reportData?.agent_data?.name?.length || reportData?.agent_data?.warname?.length}
                headerSelected={headerSelected}
                setHeaderSelected={setHeaderSelected}
              />
            </div>
          </Template.Header>
          <Template.Body className={`${collapsed ? 'p-[0]' : ''}`}>
            <Menu buttonRef={buttonRef} toggleFilters={toggleFilters} fixedTop={fixedTop}
                  collapsed={collapsed} apply={apply} clearSearch={clearSearch}>

              <Filters setFilters={setFilters} filters={filters}/>
              <AutoDatetime setFilters={setFilters}/>

            </Menu>
          </Template.Body>
        </Template>

        <Card className={`w-full h-fit mr-2 ${!reportData ? 'mt-[59px]' : 'mt-[61px]' } print:!mt-[-15px] transform ${!reportData ? '!bg-[#f3f3f4] !border-none !-translate-y-[5px]' : ''} ${collapsed ? 'ml-2' : ''}`}>
          <Card.Body>
            <div className={`flex-1 ${!reportData ? 'p-[0]' : 'px-[20px] py-[5px]'}`}>
              <NotAppliedContent reportData={reportData}/>
              <NoDataContent reportData={reportData} data={reportData?.length}/>
              <Content reportData={reportData} data={reportData?.agent_data.name || reportData?.agent_data.warname} entity={headerSelected} title={title} collapsed={collapsed} setLoading={setLoading} printComponentRef={printComponentRef} details={details} isLeader={isLeader}/>
            </div>
          </Card.Body>
        </Card>

      </div>
    </SubScroll>
  );
};

export default AgentProductivity;
